/* eslint-disable complexity */
/**
 * Order creation and management utilities for RMC orders
 */

import {getDefaultTruckSize, getTruckSize} from '@hconnect/tuc-processes/fields/trucks/trucks.utils'
import {TruckConfiguration} from '@hconnect/tuc-processes/fields/trucks/types'
import {FieldsForm} from '@hconnect/tuc-processes/fields/types'

import {RegionFeatures} from '../config/regionFeatures'
import {RmcOrder} from '../types/order'

/**
 * Validates order data and returns validation status based on region configuration
 * This function checks only fields that are required based on the enabled steps
 */
export const validateOrderDataBasedOnRegionConfig = (
  formData: FieldsForm,
  regionFeatures: RegionFeatures,
  truckConfig?: TruckConfiguration
): boolean => {
  // Always required fields regardless of region
  const alwaysRequiredFields = [
    formData.material?.materialNumber || formData.material?.materialId,
    formData.quantity,
    formData.dateTime,
    formData.poNumber,
    formData.orderPlacer?.name,
    formData.orderPlacer?.phoneNumber,
    formData.siteContact?.name,
    formData.siteContact?.phoneNumber
  ]

  // Conditionally required fields based on region features
  const conditionalFields: (string | number | boolean | undefined | null)[] = []

  // Add concrete usage if the step is enabled in the region
  if (regionFeatures.hasConcreteUsage) {
    conditionalFields.push(formData.concreteUsage)
  }

  // Add unloading method if the step is enabled in the region
  if (regionFeatures.hasUnloadingMethod) {
    conditionalFields.push(formData.unloadingMethod?.unloadingMethodCode)
  }

  // Calculate truck capacity and determine scenario
  const quantity =
    formData.quantity.type === 'volume' ? formData.quantity.volume : formData.quantity.fullLoads
  // Use the truck configuration if provided, otherwise use backward compatibility function
  const truckCapacity = getTruckCapacity(formData, truckConfig)
  // Calculate whether this is a multiple truck scenario based on quantity vs capacity
  const isMultipleTruck =
    formData.quantity.type === 'volume' ? quantity > truckCapacity : quantity >= 2

  // Check interval fields based on truck scenario and region features
  if (isMultipleTruck) {
    // Multiple truck scenario: check spacing/flowRate if enabled
    if (regionFeatures.allowFlowRate && formData.spacingOrFlowRate?.type === 'flowRate') {
      conditionalFields.push(formData.spacingOrFlowRate.flowRate)
    }
    if (formData.spacingOrFlowRate?.type === 'loadSpace') {
      conditionalFields.push(formData.spacingOrFlowRate.loadSpace)
    }
    // Time on job site might also be required for multiple truck if alwaysHasTimeOnJobSite
    if (regionFeatures.alwaysHasTimeOnJobSite && regionFeatures.allowTimeOnJobSite) {
      conditionalFields.push(formData.timeOnJobSite?.time)
    }
  } else {
    // Single truck scenario: check timeOnJobSite if enabled
    if (regionFeatures.allowTimeOnJobSite) {
      conditionalFields.push(formData.timeOnJobSite?.time)
    }
  }

  // Driver instructions are optional even when enabled
  // No validation needed for optional fields

  // Combine all required fields
  const requiredFields = [...alwaysRequiredFields, ...conditionalFields]

  // Validate all required fields are present
  return requiredFields.every((field) => field !== undefined && field !== null && field !== '')
}

/**
 * Gets truck capacity based on form data and region features
 * Mimics the mobile app logic for determining truck capacity
 */
/**
 * Get truck capacity based on form data, truck configuration, and region features
 * Prioritizes using the unified truck configuration over legacy defaults
 */
const getTruckCapacity = (formData: FieldsForm, configuration?: TruckConfiguration): number => {
  // If a truck configuration is provided, use it to get the truck size
  if (configuration?.trucks) {
    // Get the truck size from the unified configuration
    const truckSize = getTruckSize(configuration.trucks, formData.truckType)

    // Return the configured size if available
    if (truckSize && truckSize > 0) {
      return truckSize
    }
  }

  // Fallback for backward compatibility
  // Get the default truck size from unified configuration if available
  if (configuration?.trucks) {
    const configuredDefaultSize = getDefaultTruckSize(configuration.trucks)
    if (configuredDefaultSize && configuredDefaultSize > 0) {
      return configuredDefaultSize
    }
  }

  // Ultimate fallback - standard truck capacity
  return 8
}

/**
 * Validates order data and returns validation status
 */
export const validateOrderData = (formData: FieldsForm): boolean => {
  // Check required fields
  const requiredFields = [
    formData.material?.materialNumber || formData.material?.materialId,
    formData.quantity,
    formData.concreteUsage,
    formData.unloadingMethod?.unloadingMethodCode,
    formData.dateTime,
    formData.poNumber,
    // Contact fields validation
    formData.orderPlacer?.name,
    formData.orderPlacer?.phoneNumber,
    formData.siteContact?.name,
    formData.siteContact?.phoneNumber
  ]

  return requiredFields.every((field) => field !== undefined && field !== null && field !== '')
}

/**
 * Formats material variations (slump/setControl) for display
 * Uses description if available, otherwise falls back to value
 *
 * @param order - The RMC order containing material variations
 * @returns Formatted string like "60mm, Fast Set" or null if no variations
 */
export const getMaterialVariationsDisplay = (order: RmcOrder): string | null => {
  const materialVariations = order.formData.materialVariations

  if (!materialVariations) return null

  const hasSlump =
    materialVariations.slump?.value !== undefined && materialVariations.slump?.value !== null
  const hasSetControl =
    materialVariations.setControl?.value !== undefined &&
    materialVariations.setControl?.value !== null

  if (!hasSlump && !hasSetControl) return null

  const parts: string[] = []
  if (hasSlump) {
    const slumpValue = materialVariations.slump?.description || materialVariations.slump?.value
    if (slumpValue) parts.push(slumpValue)
  }
  if (hasSetControl) {
    const setControlValue =
      materialVariations.setControl?.description || materialVariations.setControl?.value
    if (setControlValue) parts.push(setControlValue)
  }

  return parts.length > 0 ? parts.join(', ') : null
}
